import { isEqual } from 'lodash-es';
import type { NavigationGuard } from 'vue-router';

import auth from '@/modules/Auth';
import { featureFlags, userAdapter as flagsUserAdapter } from '@/modules/featureFlags';
import useUserState from '@/state/useUserState';
import useMainState from '@/state/useMainState';
import useSectionState from '@/state/useSectionState';
import useAppState from '@/state/useAppState';
import {
  setupInitialAppConfig,
  updateAppConfig,
} from '@/utils/setupInitialAppConfig';
import getDestinations from '@/utils/getDestinations';
import getUserInfo from '@/utils/getUserInfo';
import getHistoryUser from '@/utils/getHistoryUser';
import { getUserTypeDefaultRedirect } from '@/utils/getUserTypeDefaultRedirect';
import { track } from '@/utils/analytics';
import useRequestCache from '@/utils/useRequestCache';
import navigate from '@/utils/navigate';
import getUserCompsets from '@/utils/getUserCompsets';
import getKeyFromObject from '@/utils/getKeyFromObject';
import sectionExceptions from '@/utils/sectionExceptions';
import handleOnPropertyChange from '@/utils/handleOnPropertyChange';
import isAbortError from '@/utils/isAbortError';
import clearLocalStorage from '@/utils/clearLocalStorage';
import { productTour } from '@/modules/productTour';

const env = import.meta.env || {};

const {
  isStrictlyDMOUser,
  isKeyUser,
  userData,
} = useUserState();

const {
  minPropertiesToShowCountdown,
} = useMainState();

const {
  filtersQuery,
  setSectionName,
  setIsBenchDirectLoading,
  sectionName,
  setRunningCalls,
} = useSectionState();

const { setAppData } = useAppState();

const { initRequestCache, clearCacheStorage } = useRequestCache();

export const beforeGuard: NavigationGuard = async (to, from, next) => {
  if (to.meta.noUser) {
    next();
    return;
  }

  let shouldClearCache = false;

  if (to.query.loginAs) {
    auth.loginAs(to.query.loginAs.toString(), false);
    clearLocalStorage();
    shouldClearCache = true;
  }

  if (!await auth.isAuthenticated()) {
    auth.login();
    track('login');
    return;
  }

  if (!userData.email) {
    let userInfo;
    let userHistory;

    try {
      userInfo = await getUserInfo();
      if (userInfo.user.type === 'internal') {
        navigate(`${env.VUE_APP_NAKAMA_URL}/redirect/latte`);
      }
    } catch (err: unknown) {
      if (
        err
        && typeof err === 'object'
        && 'response' in err
        && err.response
        && typeof err.response === 'object'
        && 'status' in err.response
        && err.response.status === 401
      ) {
        navigate(`${env.VUE_APP_NAKAMA_URL}?no-access=benchdirect`, { external: true });
      }

      next({ name: 'error' });
      return;
    }

    try {
      userHistory = await getHistoryUser(userInfo.user.id);
    } catch {
      throw new Error('User history not available');
    }

    await featureFlags.init(
      flagsUserAdapter(userInfo),
    );
    setAppData(userInfo);
    setAppData({
      user: {
        type: userHistory.type,
        role: userHistory.default.role_name,
        demand: !!userHistory.active_products.demand,
        safe_direct: !!userHistory.active_products.safe_direct,
        predictive_personalization: !!userHistory.active_products.predictive_personalization,
        predictive_audience: !!userHistory.active_products.predictive_audience,
        ernest: !!userHistory.active_products.ernest,
        accountId: userHistory.default.account_id,
      },
    });
    await setupInitialAppConfig();
    await initRequestCache();

    if (shouldClearCache) {
      await clearCacheStorage();
    }
  }

  if (to.path === '/') {
    next(getUserTypeDefaultRedirect());
    return;
  }

  setSectionName(to.name);

  const matchedRoute = to.matched[0];
  if (!matchedRoute) {
    next();
    return;
  }

  const {
    requiresStrictDMOUser,
    requiresPropertyUser,
    requiresDataAccess,
    requiresKeyUser,
    isDataRoute,
  } = matchedRoute.meta;

  if (requiresDataAccess) {
    if (isStrictlyDMOUser.value) {
      const destinations = await getDestinations();

      if (!destinations.length) {
        next('/destinations');
        return;
      }
    } else if (!minPropertiesToShowCountdown.value) {
      if (!isKeyUser()) {
        next('/waiting-room');
        return;
      }

      next('/onboarding');
      return;
    }
  }

  if ((requiresKeyUser && !isKeyUser())
  || (requiresStrictDMOUser && !isStrictlyDMOUser.value)
  || (requiresPropertyUser && isStrictlyDMOUser.value)) {
    next(getUserTypeDefaultRedirect());
    return;
  }

  const hasPropertyParam = to.matched
    .some((matchedRoute) => matchedRoute.path?.match(/:propertyId\?$/i));

  let updateRouteData;

  if (!hasPropertyParam) {
    delete to.params.propertyId;
    to.query = {};
  } else {
    if (!to.params.type && from.params.type) {
      updateRouteData = true;
      to.params.type = from.params.type;
    }

    if (to.params.type !== 'overview' && !to.params.propertyId && from.params.propertyId) {
      updateRouteData = true;
      to.params.propertyId = from.params.propertyId;
    }

    if (isDataRoute
      && Object.keys(filtersQuery.value).length
      && (!isEqual(filtersQuery.value, to.query))) {
      updateRouteData = true;
    }
  }

  // if route has to keep query && stored query && to query is not that
  if (updateRouteData) {
    next({ name: to.name, query: filtersQuery.value, params: to.params });
  } else {
    next();
  }
};

export const afterGuard: NavigationGuard = async (to, from) => {
  document.getElementById('mainSectionElementRef')?.scrollTo({ top: 0, behavior: 'smooth' });

  if (from.path !== to.path) {
    const { isInternalUser } = useSectionState();
    if (!isInternalUser.value) {
      productTour.trackPage();
    }
    setIsBenchDirectLoading(true);
    await updateAppConfig();

    if (to.meta.isDataRoute) {
      setRunningCalls();

      if (from.params.propertyId !== to.params.propertyId) {
        try {
          await getUserCompsets();
        } catch (err: unknown) {
          if (err instanceof Error) {
            isAbortError(err);
          }
          return;
        }
      }

      setIsBenchDirectLoading(false);

      handleOnPropertyChange({
        userInput: true,
        disableStats: !!getKeyFromObject(sectionExceptions, sectionName.value).disableStats,
      });
    }
    setIsBenchDirectLoading(false);
  }
};
